import { Component, Input, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AnalyticEvents } from '@app/enums';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { BaseForm } from '@forms/base.form';
import { Employee } from '@models/employee/employee.model';
import { Goal } from '@models/performance/goal.model';

@Component({
    selector: 'app-form-goal-participants',
    templateUrl: './goal-participants.template.html',
    styleUrls: ['./goal-participants.style.scss'],
})
export class GoalParticipantsForm extends BaseForm implements OnInit {
    @Input() goal: Goal;
    @Input() participants: Map<number, boolean>;
    isLoading = true;
    self: Employee;
    peers: Employee[] = [];
    directReports: Employee[] = [];

    constructor(
        public auth: AuthService,
        private analyticService: AnalyticService
    ) {
        super();
    }

    selectAll(group: Employee[]) {
        group.forEach((e) => this.participants.set(e.id, true));
    }

    deselectAll(group: Employee[]) {
        group.forEach((e) => this.participants.set(e.id, false));
    }

    trackSelectingSelfAsParticipant(event: MatCheckboxChange): void {
        if (event.checked) {
            this.analyticService.trackEvent(
                AnalyticEvents.PerformanceAllPathsToCreateGoalStep3SelectYourselfAsParticipant
            );
        }
    }

    trackSelectingOfParticipantsWhoHaveDirectReports(event: MatCheckboxChange): void {
        if (event.checked) {
            this.analyticService.trackEvent(
                AnalyticEvents.PerformanceAllPathsToCreateGoalStep3SelectDirectReportsAsParticipants
            );
        }
    }

    trackSelectingOfParticipantsWhoArePeers(event: MatCheckboxChange): void {
        if (event.checked) {
            this.analyticService.trackEvent(
                AnalyticEvents.PerformanceAllPathsToCreateGoalStep3SelectPeersAsParticipants
            );
        }
    }

    async ngOnInit(): Promise<void> {
        this.self = this.auth.employee;
        this.directReports = await this.getDirectReports();
        this.peers = await this.getPeers();

        [...this.directReports, ...this.peers].forEach((employee) => this.participants.set(employee.id, false));

        // Auto assign self.
        this.participants.set(this.self.id, true);
        this.isLoading = false;
    }

    private async getDirectReports(): Promise<Employee[]> {
        const [directReports] = await Employee.param('company', this.auth.company.id)
            .where('reportsTo', this.auth.employee.id)
            .orderBy('lastName', 'ASC')
            .all();
        return directReports;
    }

    private async getPeers(): Promise<Employee[]> {
        if (!this.auth.employee.reportsTo) {
            return [];
        }

        const [peers] = await Employee.param('company', this.auth.company.id)
            .where('reportsTo', this.auth.employee.reportsTo)
            .orderBy('lastName', 'ASC')
            .all();

        // dont include self in list of peers, shows auth'd employee in duplicate locations
        return peers.filter((peer: Employee) => this.auth.employee.isNot(peer));
    }
}
